import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import ConfirmModal from "../../components/ConfirmModal";
import { KeepoLoader } from "../../components/KeepoLoader";
import { Box } from "../../components/Layout";
import { ScreenHeader } from "../../components/SubHeader";
import constants from "../../constants";
import { useAppState } from "../../contexts/App";
import PlusIcon from "../../icons/Plus";
import SymbolKeepoIcon from "../../icons/SymbolKeepo";
import { createNewSite, Site } from "../../model/site";
import { api, ApiSiteResponse, hasActiveSubscription, Subscription } from "../../services/api";
import { TrackingIntegrations } from "../../services/api/models";
import { customDomainService } from "../../services/customDomain";
import { clearPlusRequiredCreation, markPlusRequiredCreation } from "../../utils/plusRequiredCreation";
import { classNames } from "../../utils/styles";
import { uuidv4 } from "../../utils/uuid";
import { ActiveSubscriptionBanner } from "./components/ActiveSubscriptionBanner";
import CardSite from "./components/CardSite";
import { EmptyState } from "./components/EmptyState";
import LoadingState from "./components/LoadingState";
import NpsPrompt from "./components/NpsPrompt";
import { PremiumBanner } from "./components/PremiumBanner";
import { Container, HeaderBox, LoadingMore, LogoSection, NewSiteCard, SitesSection } from "./styles";

type Props = {
  setSite: (site: Site) => void;
};

const showPremiumBannerLocalStorage = localStorage.getItem("premium-banner");

const SitesPage = ({ setSite }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { appState, setAppState } = useAppState();

  // The Keepos as the API orders them, 30 at a time: published PLUS, published FREE, then the ones being created
  // (never published), listed with a "Rascunho" tag and resumed with a tap. FREE drafts can be discarded; PLUS ones
  // (subscribed while creating, left before publishing) can't, they're paid for
  const [keepos, setKeepos] = useState<ApiSiteResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // The last page came (it had fewer than a whole page)
  const [reachedEnd, setReachedEnd] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  // Whether any Keepo, loaded or not, is still FREE (the PLUS banner)
  const [hasFreeKeepo, setHasFreeKeepo] = useState(false);
  const [draftDiscarding, setDraftDiscarding] = useState<ApiSiteResponse>();

  const [draftAvailableToActiveOnBaner, setDraftAvailableToActiveOnBaner] = useState<ApiSiteResponse>();

  const [siteRestoringDraft, setSiteRestoringDraft] = useState<ApiSiteResponse>();

  const [showMessageConfirmCreatingPlus, setShowMessageConfirmCreatingPlus] = useState(false);
  // Source of truth is the server (only PUBLISHED, non-PLUS Keepos consume a slot -
  // see `Site::getFreeSitesCount` on the backend). Defaults to permissive so a
  // transient issue reading it never blocks the UI - the server still enforces the
  // real limit on publish/enable regardless of this client-side gate.
  const [canCreateFreeSite, setCanCreateFreeSite] = useState(true);

  const [loadingPage, setLoadingPage] = useState(true);
  // Connected custom domain of each PLUS Keepo (by site id), filled in as its page shows
  const [customDomains, setCustomDomains] = useState<Record<string, string>>({});

  const [user, setUser] = useState<string>();

  const [showPremiumBanner, setShowPremiumBanner] = useState(() => {
    return showPremiumBannerLocalStorage === "displayed";
  });

  const createNewSiteHandler = useCallback(() => {
    // Always a new Keepo (a draft in progress is resumed from its own card). Past the free limit it
    // goes through the flow all the same, marked as PLUS: the draft can be saved, and subscribing is
    // asked for (and explained) when publishing, in /subscription/features
    const siteId = uuidv4();
    if (!canCreateFreeSite) {
      markPlusRequiredCreation(siteId);
    }

    const site = createNewSite();

    setSite(site);
    setAppState({
      ...appState,
      editingSiteId: siteId,
      editingSiteModified: false,
      published: false,
      editingSiteDisabled: false,
      editingSiteSubscription: { type: "FREE" },
    });

    navigate("/editor/title");
  }, [appState, canCreateFreeSite, navigate, setAppState, setSite]);

  const onClickEditCardSite = (site: ApiSiteResponse) => {
    if (site.draft) {
      setSiteRestoringDraft(site);
    } else {
      edit(
        site.site,
        site.siteId,
        "/keepo",
        false,
        true,
        site.disabled,
        site.subscription,
        site.tracking,
        site.passwordProtected,
        !!site.ageGate?.enabled
      );
    }
  };

  const confirmUseDraftAndEdit = () => {
    const { siteId, draft, disabled, subscription, tracking, passwordProtected, ageGate } =
      siteRestoringDraft as ApiSiteResponse;
    const route = draft.title ? "/keepo" : "/editor/title";
    edit(draft, siteId, route, true, true, disabled, subscription, tracking, passwordProtected, !!ageGate?.enabled);
  };

  const deleteDraftAndEdit = () => {
    const { site, siteId, disabled, subscription, tracking, passwordProtected, ageGate } =
      siteRestoringDraft as ApiSiteResponse;
    api.deleteDraf(siteId);
    edit(site, siteId, "/keepo", false, true, disabled, subscription, tracking, passwordProtected, !!ageGate?.enabled);
  };

  const resumeDraft = (item: ApiSiteResponse) => {
    const { siteId, draft, disabled, subscription, tracking, passwordProtected, ageGate } = item;
    const route = draft.title ? "/editor/pages" : "/editor/title";
    edit(draft, siteId, route, true, false, disabled, subscription, tracking, passwordProtected, !!ageGate?.enabled);
  };

  const discardDraft = () => {
    const { siteId } = draftDiscarding as ApiSiteResponse;
    api.deleteDraf(siteId); //call and forget
    // The site never had a published `site`, so discarding its only draft removes it server-side
    // entirely - drop it locally too
    setKeepos(prev => prev.filter(item => item.siteId !== siteId));
    setTotal(count => count - 1);
    clearPlusRequiredCreation(siteId);
    setDraftDiscarding(undefined);
  };

  const edit = useCallback(
    (
      site: Site,
      siteId: string,
      nextPage: string,
      editingSiteModified: boolean,
      published: boolean,
      editingSiteDisabled: boolean,
      subscription: Subscription = { type: "FREE" },
      trackingIntegrations?: TrackingIntegrations,
      passwordProtected = false,
      ageGated = false
    ) => {
      setAppState({
        ...appState,
        editingSiteId: siteId,
        editingSiteModified,
        published,
        editingSiteDisabled,
        notRedirectOnEmptySites: true,
        editingSiteSubscription: subscription,
        editingSiteTrackingIntegrations: trackingIntegrations,
        editingSitePasswordProtected: passwordProtected,
        editingSiteAgeGated: ageGated,
      });

      setSite(site);
      navigate(nextPage);
    },
    [appState, navigate, setAppState, setSite]
  );

  const confirmUsePlusDraftNonPublishedSite = useCallback(() => {
    const { siteId, draft, disabled, subscription, tracking, passwordProtected, ageGate } =
      draftAvailableToActiveOnBaner as ApiSiteResponse;
    edit(
      draft,
      siteId,
      "/editor/title",
      true,
      false,
      disabled,
      subscription,
      tracking,
      passwordProtected,
      !!ageGate?.enabled
    );
  }, [draftAvailableToActiveOnBaner, edit]);

  const loadSites = async () => {
    try {
      const [first, userInfo] = await Promise.all([api.getSitesPage(1), api.getUser()]);

      setUser(userInfo.givenName);
      setCanCreateFreeSite(
        typeof userInfo.canCreateFreeSite === "boolean"
          ? userInfo.canCreateFreeSite
          : (userInfo.freeSitesCount ?? 0) < constants.freeKeeposLimit
      );

      setAppState({ ...appState, isPROUser: first.summary.isPROUser });

      // A PLUS subscription with no Keepo made yet: the "Assinatura disponível" banner offers to create it
      setDraftAvailableToActiveOnBaner(first.summary.plusWithoutKeepo ?? undefined);
      setHasFreeKeepo(first.summary.hasFreeKeepo);

      setKeepos(first.items);
      setTotal(first.total);
      setReachedEnd(first.items.length < first.perPage);
    } finally {
      setLoadingPage(false);
    }
  };

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    api
      // From how many are shown, not from the page's number: the list moves under it (a draft discarded) and one
      // would be skipped
      .getSitesPage(page + 1, keepos.length)
      .then(next => {
        // A Keepo already shown (the list moved, e.g. a draft discarded meanwhile) isn't shown twice
        setKeepos(shown => [
          ...shown,
          ...next.items.filter(item => !shown.some(keepo => keepo.siteId === item.siteId)),
        ]);
        setTotal(next.total);
        setReachedEnd(next.items.length < next.perPage);
        setPage(page + 1);
      })
      // Stops asking (the list's end would ask again right away); a reload tries again
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [page, keepos.length]);

  // More Keepos as the list scrolls: the next page loads when its end shows; at the last page nothing more loads,
  // and nothing is said. A page that comes short is the last one: the Keepos shown may never add up to the total when
  // the list moves under it (a draft discarded), and the end would ask for empty pages forever
  const hasMore = !loadingPage && keepos.length < total && !reachedEnd && !moreFailed;
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = listEnd.current;
    if (!hasMore || loadingMore || typeof window.IntersectionObserver !== "function" || !end) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  const onAddNewSiteHandler = useCallback(() => {
    if (draftAvailableToActiveOnBaner) {
      setShowMessageConfirmCreatingPlus(true);
      return;
    }

    createNewSiteHandler();
  }, [draftAvailableToActiveOnBaner, createNewSiteHandler]);

  useEffect(() => {
    loadSites();
  }, []);

  // `GET /sites` doesn't bring the custom domain: ask for it for each published PLUS Keepo (only PLUS can have one),
  // once, as its page shows
  const domainsAsked = useRef(new Set<string>());
  useEffect(() => {
    const plusSites = keepos.filter(
      ({ siteId, site, subscription }) =>
        !!site && hasActiveSubscription(subscription) && !domainsAsked.current.has(siteId)
    );
    plusSites.forEach(({ siteId }) => {
      domainsAsked.current.add(siteId);
      customDomainService
        .get(siteId)
        .then(domain => {
          if (domain?.status === "connected") {
            setCustomDomains(shown => ({ ...shown, [siteId]: domain.domain }));
          }
        })
        .catch(() => undefined);
    });
  }, [keepos]);

  // "Create Keepo" from the sidebar lands here with a flag in the navigation state.
  // It is cleared right away so a reload or going back doesn't start the flow again.
  useEffect(() => {
    if (loadingPage || !(location.state as { createNew?: boolean } | null)?.createNew) {
      return;
    }
    navigate(location.pathname, { replace: true, state: null });
    onAddNewSiteHandler();
  }, [loadingPage, location, navigate, onAddNewSiteHandler]);

  useEffect(() => {
    if (!loadingPage && showPremiumBannerLocalStorage !== "displayed") {
      const timer = setTimeout(() => {
        localStorage.setItem("premium-banner", "displayed");
        setShowPremiumBanner(true);
      }, 750);

      return () => clearTimeout(timer);
    }
  }, [loadingPage, showPremiumBannerLocalStorage]);

  return (
    <>
      {loadingPage ? (
        <LoadingState />
      ) : (
        <Container>
          <HeaderBox>
            <LogoSection>
              <SymbolKeepoIcon letters={false} />
            </LogoSection>
            <div className="greeting">
              <h4>
                {t("pages.sites.intro.hello")}, {user} 👋
              </h4>
            </div>
          </HeaderBox>

          {keepos.length > 0 && <ScreenHeader title={t("pages.sites.list.title")} />}

          {draftAvailableToActiveOnBaner && (
            <Box>
              <ActiveSubscriptionBanner onClick={confirmUsePlusDraftNonPublishedSite} />
            </Box>
          )}
          {/* Offered while any Keepo, published or draft, is still FREE (not with a subscription waiting above) */}
          {!draftAvailableToActiveOnBaner && hasFreeKeepo && (
            <PremiumBanner visible={showPremiumBanner} draftAvailable={!!draftAvailableToActiveOnBaner} />
          )}

          {keepos.length > 0 ? (
            <Box
              className={classNames({
                npt: !!draftAvailableToActiveOnBaner,
              })}>
              <SitesSection>
                {/* First, so creating a Keepo never needs scrolling past a long list */}
                <NewSiteCard type="button" onClick={onAddNewSiteHandler}>
                  <span className="icon">
                    <PlusIcon width={14} height={14} color="currentColor" />
                  </span>
                  {t("pages.sites.actions.createNew.button.title")}
                </NewSiteCard>
                {keepos.map(item =>
                  item.site ? (
                    <CardSite
                      key={item.siteId}
                      site={item.site}
                      disabled={item.disabled}
                      onEditClick={() => onClickEditCardSite(item)}
                      subscription={item.subscription}
                      customDomain={customDomains[item.siteId]}
                    />
                  ) : (
                    // Drafts last, as the API sends them (PLUS first, then oldest first)
                    <CardSite
                      key={item.siteId}
                      site={item.draft}
                      disabled={false}
                      draft
                      subscription={item.subscription}
                      onEditClick={() => resumeDraft(item)}
                      onDiscardClick={item.subscription?.type === "PLUS" ? undefined : () => setDraftDiscarding(item)}
                    />
                  )
                )}
              </SitesSection>
              <div ref={listEnd} />
              {loadingMore && (
                <LoadingMore role="status" aria-label={t("pages.sites.list.loadingMore")}>
                  <KeepoLoader size={20} color="var(--color-neutral-white)" />
                </LoadingMore>
              )}
            </Box>
          ) : (
            <EmptyState onCreate={onAddNewSiteHandler} />
          )}
          {siteRestoringDraft && (
            <ConfirmModal
              type="INFO"
              title={t("pages.sites.actions.restoreDraft.modal.title").replace(
                "${name}",
                siteRestoringDraft.site.title
              )}
              confirmButtonTitle={t("pages.sites.actions.restoreDraft.modal.confirm")}
              cancelTitle={t("pages.sites.actions.restoreDraft.modal.cancel")}
              onExit={() => setSiteRestoringDraft(undefined)}
              onConfirm={confirmUseDraftAndEdit}
              onCancel={deleteDraftAndEdit}
            />
          )}
          {draftDiscarding && (
            <ConfirmModal
              type="DANGER"
              title={t("pages.sites.card.draft.confirmTitle")}
              descriptions={[t("pages.sites.card.draft.confirmDescription")]}
              confirmButtonTitle={t("pages.sites.card.draft.confirm")}
              cancelTitle={t("pages.sites.card.draft.cancel")}
              onConfirm={discardDraft}
              onCancel={() => setDraftDiscarding(undefined)}
            />
          )}
          {showMessageConfirmCreatingPlus && draftAvailableToActiveOnBaner && (
            <ConfirmModal
              type="INFO"
              title={t("pages.sites.actions.warningUsingPlusKeepo.title")}
              cancelTitle={t("pages.sites.actions.warningUsingPlusKeepo.cancel")}
              confirmButtonTitle={t("pages.sites.actions.warningUsingPlusKeepo.confirm")}
              onConfirm={confirmUsePlusDraftNonPublishedSite}
              onCancel={() => setShowMessageConfirmCreatingPlus(false)}
            />
          )}
          {/* From time to time, a while after the list shows (the API decides who's due) */}
          <NpsPrompt />
        </Container>
      )}
    </>
  );
};

export default SitesPage;
